import React, { useState } from 'react';
import { 
  FiX, 
  FiCalendar, 
  FiUsers, 
  FiMapPin, 
  FiTag, 
  FiAlertTriangle,
  FiRefreshCw,
  FiFilter
} from 'react-icons/fi';
import { SnagFilters as FiltersType } from '../../../shared/types/snag';
import './SnagFilters.css';

interface SnagFiltersProps {
  filters: FiltersType;
  onFiltersChange: (filters: FiltersType) => void;
  onClose: () => void;
}

const PRIORITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
const ASSIGNEE_OPTIONS = [
  { id: '1', name: 'John Smith', trade: 'Tiling' },
  { id: '3', name: 'Mike Chen', trade: 'Painting' },
  { id: '5', name: 'Tom Anderson', trade: 'Electrical' },
  { id: '7', name: 'Sarah Johnson', trade: 'Plumbing' },
  { id: '9', name: 'David Wilson', trade: 'Carpentry' }
];

const LOCATION_OPTIONS = [
  'Block A - 1st Floor',
  'Block A - 2nd Floor', 
  'Block B - 1st Floor',
  'Block B - 2nd Floor',
  'Common Areas',
  'Parking Garage'
];

const TAG_OPTIONS = [
  'Electrical', 'Plumbing', 'Tiling', 'Painting', 'Carpentry',
  'HVAC', 'Safety', 'Finishing', 'Structural', 'Flooring'
];

const SnagFilters: React.FC<SnagFiltersProps> = ({ filters, onFiltersChange, onClose }) => {
  const [localFilters, setLocalFilters] = useState<FiltersType>(filters);

  const handlePriorityChange = (priority: string) => {
    const updatedPriorities = localFilters.priority.includes(priority)
      ? localFilters.priority.filter(p => p !== priority)
      : [...localFilters.priority, priority];
    
    setLocalFilters({ ...localFilters, priority: updatedPriorities });
  };

  const handleAssigneeChange = (assigneeId: string) => {
    const updatedAssignees = localFilters.assignedTo.includes(assigneeId)
      ? localFilters.assignedTo.filter(a => a !== assigneeId)
      : [...localFilters.assignedTo, assigneeId];
    
    setLocalFilters({ ...localFilters, assignedTo: updatedAssignees });
  };

  const handleLocationChange = (location: string) => {
    const updatedLocations = localFilters.location.includes(location)
      ? localFilters.location.filter(l => l !== location)
      : [...localFilters.location, location];
    
    setLocalFilters({ ...localFilters, location: updatedLocations });
  };

  const handleTagChange = (tag: string) => {
    const updatedTags = localFilters.tags.includes(tag)
      ? localFilters.tags.filter(t => t !== tag)
      : [...localFilters.tags, tag];
    
    setLocalFilters({ ...localFilters, tags: updatedTags });
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    setLocalFilters({
      ...localFilters,
      dateRange: { ...localFilters.dateRange, [field]: value }
    });
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
  };

  const clearAllFilters = () => {
    const clearedFilters: FiltersType = {
      search: '',
      priority: [],
      assignedTo: [],
      location: [],
      tags: [],
      dateRange: {}
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const getActiveFilterCount = () => {
    return localFilters.priority.length + 
           localFilters.assignedTo.length + 
           localFilters.location.length + 
           localFilters.tags.length +
           (localFilters.dateRange.start ? 1 : 0) +
           (localFilters.dateRange.end ? 1 : 0);
  };

  return (
    <div className="snag-filters-overlay">
      <div className="snag-filters-panel">
        <div className="filters-header">
          <div className="filters-title">
            <FiFilter />
            <h3>Filter Snags</h3>
            {getActiveFilterCount() > 0 && (
              <span className="active-filters-count">{getActiveFilterCount()}</span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="filters-content">
          {/* Priority Filter */}
          <div className="filter-section">
            <div className="filter-section-header">
              <FiAlertTriangle />
              <h4>Priority</h4>
            </div>
            <div className="filter-options">
              {PRIORITY_OPTIONS.map(priority => (
                <label key={priority} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={localFilters.priority.includes(priority)}
                    onChange={() => handlePriorityChange(priority)}
                  />
                  <span className={`checkbox-custom priority-${priority.toLowerCase()}`}></span>
                  <span className="checkbox-label">{priority}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Assignee Filter */}
          <div className="filter-section">
            <div className="filter-section-header">
              <FiUsers />
              <h4>Assigned To</h4>
            </div>
            <div className="filter-options">
              {ASSIGNEE_OPTIONS.map(assignee => (
                <label key={assignee.id} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={localFilters.assignedTo.includes(assignee.id)}
                    onChange={() => handleAssigneeChange(assignee.id)}
                  />
                  <span className="checkbox-custom"></span>
                  <div className="assignee-option">
                    <span className="assignee-name">{assignee.name}</span>
                    <span className="assignee-trade">{assignee.trade}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Location Filter */}
          <div className="filter-section">
            <div className="filter-section-header">
              <FiMapPin />
              <h4>Location</h4>
            </div>
            <div className="filter-options">
              {LOCATION_OPTIONS.map(location => (
                <label key={location} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={localFilters.location.includes(location)}
                    onChange={() => handleLocationChange(location)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="checkbox-label">{location}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Tags Filter */}
          <div className="filter-section">
            <div className="filter-section-header">
              <FiTag />
              <h4>Tags</h4>
            </div>
            <div className="filter-tags">
              {TAG_OPTIONS.map(tag => (
                <button
                  key={tag}
                  className={`tag-filter ${localFilters.tags.includes(tag) ? 'selected' : ''}`}
                  onClick={() => handleTagChange(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="filter-section">
            <div className="filter-section-header">
              <FiCalendar />
              <h4>Date Range</h4>
            </div>
            <div className="date-range-inputs">
              <div className="date-input">
                <label>From</label>
                <input
                  type="date"
                  value={localFilters.dateRange.start || ''}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                />
              </div>
              <div className="date-input">
                <label>To</label>
                <input
                  type="date"
                  value={localFilters.dateRange.end || ''}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="filters-footer">
          <button className="clear-btn" onClick={clearAllFilters}>
            <FiRefreshCw />
            Clear All
          </button>
          <button className="apply-btn" onClick={applyFilters}>
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnagFilters;
